// @ts-check
import { useCallback, useMemo, useState } from 'react';
import { usePushServiceSubscription } from '../PushService/PushService';
import _ from 'lodash';
import useAPI from '../useAPI';
import { TimeBucket } from 'services/constants';
import createQueryRequest from 'services/createQueryRequest';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

/**
 * @param {string} timestamp
 * @returns {number} milliseconds
 */
const timestampToMillis = (timestamp) => DateTime.fromISO(timestamp).toMillis();

/**
 * @typedef {Object} TelemetrySignal
 * @property {string} entityId
 * @property {string} telemetryName
 * @property {string} timestamp
 * @property {number} value
 */
/**
 * @typedef {Object} TelemetryData
 * @property {string} signalId
 * @property {string} componentName
 * @property {string} unit
 * @property {string} type
 * @property {Array<[timeInMilliseconds: number, value: number]>} dataPoints
/**
 * @typedef {Object} TelemetryPushServiceProps
 * @property {Object} filterOptions contains filters for the received data, currently supporting assetId as list
 * @property {Object[]} [telemetryModels] if DTDL content models are included merge values into models as model.values
 */

/**
 * @param {TelemetryPushServiceProps} props
 * @returns {[TelemetryData[], isLoading: boolean, hasError: boolean]} filteredTelemetry or last values of filteredTelemetry inside TelemetryModels
 */
const useTelemetryPushService = ({ filterOptions, telemetryModels }) => {
  const [telemetry, setTelemetry] = useState([]);
  const [hasError] = useState(false);
  const api = useAPI();

  const signalMap = useMemo(() => {
    return _.flatten(_.map(filterOptions.assetIds, assetId => {
      return _.map(telemetryModels, model => {
        return {
          assetId,
          assetPath: '',
          componentName: model.componentName,
          signalId: model.name,
          signalDisplayName: model.displayName,
          type: model.schema,
          unit: model.unit,
          aggregate: TimeBucket.RAW,
          decimalPoint: 2
        };
      });
    }));
  }, [filterOptions.assetIds, telemetryModels]);

  const lastTelemetryQuery = useQuery(['lastTelemetry', filterOptions.assetIds],
    createQueryRequest(api.telemetry.telemetryForAssets),
    { enabled: !_.isEmpty(signalMap) && !_.isEmpty(filterOptions.assetIds),
      onSuccess: (/** @type {Array<TelemetrySignal[]>} */result) => {
        const map = _.map(signalMap, signal => {
          const telemetryName = (signal.componentName === null)
            ? signal.signalId
            : `${signal.componentName}.${signal.signalId}`;
          const lastTelemetry = _.flatten(result);
          const lastTelemetryForSignal = _.find(lastTelemetry, { entityId: signal.assetId, telemetryName: telemetryName });
          if (!lastTelemetryForSignal) {
            return { ...signal, dataPoints: [] };
          }
          return {
            ...signal,
            dataPoints: [[timestampToMillis(lastTelemetryForSignal?.timestamp), lastTelemetryForSignal?.value]]
          };
        });
        setTelemetry(map);
      }
    });

  const telemetryPushCallback = useCallback(pushData => {

    const telemetryMessage = pushData.message;
    const telemetryName = Object.keys(telemetryMessage)[0];
    const assetId = telemetryMessage.entityId;
    const componentName = telemetryMessage.componentName;
    const dataPoint = [timestampToMillis(telemetryMessage.timestamp), telemetryMessage[telemetryName]];

    if (_.isArray(telemetryMessage)) {
      console.error('Telemetry message is array, not supported');
    }
    setTelemetry(prevTelemetry => {
      const signal = _.find(prevTelemetry, { assetId, componentName, signalId: telemetryName });
      if (signal) {
        const remaining = _.reject(prevTelemetry, { assetId, componentName, signalId: telemetryName });
        setTelemetry([...remaining, { ...signal, dataPoints: [dataPoint] }]);
      } else {
        return prevTelemetry;
      }
    });
  }, []);

  const [isLoadingPushService, hasErrorInPushService] = usePushServiceSubscription(
    'telemetry',
    telemetryPushCallback,
    filterOptions?.assetIds);

  return [
    telemetry,
    lastTelemetryQuery.isLoading || isLoadingPushService,
    hasError || hasErrorInPushService
  ];

};

export { useTelemetryPushService };
